import { CountryCode } from 'libphonenumber-js'
import { Action } from 'redux'
import { IConditionHolder } from './IConditionHolder'
import { FormActions } from './FormActions'
import { InputTypes } from './InputTypes'
import { IOption } from './IOption'
import { IValidationCode } from './IValidationCode'
import { IFormError } from './IFormError'

export interface IFieldSetSettings extends IConditionHolder {
	formName: string
	controls: Array<IFormControl>
	fieldSetName: string
	description?: string
	tooltip?: string
	collapsable?: boolean
	iconSrc?: string
}

export interface IFormMeta {
	formName: string
	pageId: number
	product: string
	market: string
	submitDataUrl: string
	signingId: number
	submitButtonText: string
	progressBarTheme: boolean
	visualTheme: string
	requiredMessage: string
	getDataUrl: string
	validationErrorList: boolean
	validationSectionTitle: string
	identityServerBaseUrl: string
	signingApiBaseUrl: string
	callbackUrl: string
	channel?: string
	externalPartner?: string
	enableCrestt: boolean
	actions: IFormAction[]
	locale: string
	[key: string]: any
	isMultiStep?: boolean
}

export interface IFormSettings {
	controls: FormSettingsControls[]
	formMeta: IFormMeta
}

export interface IControlProps {
	formName: string
	controlName: string
}

export interface IFormStepProps extends IConditionHolder {
	grid: string
	backButton: string
	nextButton: string
	controls: Array<IFormControl | IFieldSetSettings>
}

export interface IFormAction {
	action: keyof typeof FormActions
	actionButtonText: string
	actionButtonStyle: string
	pageUrl: string
	step: number
}

export type FormSettingsControls =
	| IFormControl
	| IFieldSetSettings
	| IFormStepProps

export const hasFormStep = (form: IFormSettings) =>
	form.controls?.[0] && 'grid' in form.controls[0]

export const isFormStep = (
	props: FormSettingsControls,
): props is IFormStepProps => 'grid' in props

export const isFieldSet = (
	props: FormSettingsControls,
): props is IFieldSetSettings => 'fieldSetName' in props

export type ControlSpecificValidation = (
	control: IFormControl,
	appState: IAppState,
) => void

export interface IViewGroup {
	id: string
	/** Combined label shown in view mode. Required on the primary control. */
	label?: string
	/** This control renders the combined group row in view mode. */
	isPrimary: boolean
	/** Control names whose values are passed to formatValue (primary only). */
	displayFields?: string[]
	/** How to format the combined display value. Defaults to joining non-empty values with a space. */
	formatValue?: (values: Record<string, string>) => string
}

export interface IFormControl extends IConditionHolder {
	addBlockButton?: string
	controlName: string
	type: InputTypes
	isValid: boolean
	label: string
	required: boolean
	formMeta?: Readonly<IFormMeta>
	placeholder?: string
	validationMessage?: string
	options?: IOption[]
	basicOptions?: IOption[]
	/**
	 * @description needed for calculating progress ratio
	 */
	inStep: number
	readonly?: boolean
	description?: string
	tooltip?: string
	specificValidation?: ControlSpecificValidation
	maxLength?: number
	minLength?: number
	multiple?: boolean
	allowedFileTypes?: string
	buttonText?: string
	tilesItems?: any
	parentProps?: IFieldSetSettings | IFormControl | undefined
	invalidFilesText?: string
	validFilesText?: string
	serviceName?: string
	mapper?: string[]
	controls?: IFormControl[]
	controlInstances?: Array<IFormControl[]>
	maxItems?: number
	minItems?: number
	documentType?: string
	minValue?: number
	maxValue?: number
	stepping?: number
	/**
	 * @description the size of column
	 */
	col?: number
	/**
	 * @description the property name which will be the parent name of the control when the form is submitted
	 */
	blockName?: string
	validations?: IValidationCode[]
	minDate?: string
	maxDate?: string
	openToDate?: string
	wcagAriaLabel?: string
	/**
	 * @description neccesary for DropDown.tsx
	 */
	wrapperClass?: string
	/**
	 * @description neccesary for PhoneNumber.tsx
	 */
	countryCode?: CountryCode
	/**
	 *  @description neccesary for PhoneNumder.tsx
	 */
	disableDropdown?: boolean
	/**
	 *  @description neccesary for AddressLookup.tsx
	 */
	lookupMap?: any
	/**
	 *  @description neccesary for AddressLookup.tsx
	 */
	googleApiKey?: string
	/**
	 *  @description neccesary for AddressLookup.tsx
	 */
	useCountries?: string[]
	/**
	 *  @description neccesary for DatePicker.tsx
	 */
	disableCalendar?: boolean
	/**
	 *  @description used to display label for controls that by default do not have them
	 */
	displayFixedLabel?: boolean
	viewGroup?: IViewGroup
	inputPrefix?: string
}

export interface IWithForms {
	forms: IAppStateContainer
}

export interface IActionWithPayload<T> extends Action {
	payload: T
}

export enum FormStatus {
	inProgress,
	notValid,
	readyToSubmit,
	submitting,
	submitted,
}

export interface IAppStateContainer {
	[key: string]: IAppState
}

export interface IAppState {
	conditionalControlNames: string[]
	conditionalFieldSets: string[]
	controlsState: IAppStateControlProperty
	controlsStateSnapshot: IAppStateControlProperty | undefined
	errors: IFormError[]
	fieldSetState: IAppStateFieldSetProperty
	formMeta: Readonly<IFormMeta>
	formStatus: FormStatus
	initialSettings: Readonly<IFormSettings>
	/** @description the ratio representing the form filling progress */
	progressRatio: number[]
	step: number
	valuesToSubmit: any
}

export interface IWithFormName {
	formName: string
}

export interface IAppStateControlProperty {
	[key: string]: IFormControl
}

export interface IAppStateFieldSetProperty {
	[key: string]: IFieldSetSettings
}

export interface IUpdateFormValueActionPayload extends IWithFormName {
	controlName: string
	value: any
}

export interface IWithControlName extends IWithFormName {
	controlName: string
}

export interface IUpdateFormControllersToRepeatableControllerPayload extends IWithFormName {
	controlName: string
	newControls: Array<IFormControl[]>
	removedControls: IFormControl[] | undefined
}

export interface IMarketHelper {
	dateFormat: string // ex: yyyy-MM-dd
	marketRegex: RegExp // ex: /^\d\d\.\d\d\.\d\d\d\d$/
	mainDelimiter: string // ex: "."
	indexPositions: { day: number; month: number; year: number }
}
